import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Pencil, Sparkles, Trash2, X } from 'lucide-react';
import { Avatar } from '../ProjectPage/ui';

// Un objectif sur le tableau du kickoff : son titre (modifiable), ses tokens, son sprint, son responsable, et ce que
// Nexus propose (tokens estimés, reformulation du titre). Il se glisse d'une colonne à l'autre ; la pastille de
// l'étape en cours s'ouvre aussi en liste (au doigt, ou sans glisser).

const STATUS_DOT = { in_progress: 'bg-amber-400', blocked: 'bg-rose-500' };
export const points = (n) => `${n} pt${Math.abs(n) > 1 ? 's' : ''}`;

const ChipSelect = ({ label, title, value, options, onChange, tone = 'bg-slate-100 text-slate-600' }) => (
  <label title={title} className={`relative inline-flex max-w-[9rem] cursor-pointer items-center rounded-full px-1.5 py-px text-[11px] font-medium ${tone}`}>
    <span className="truncate">{label}</span>
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} aria-label={title}
      className="absolute inset-0 cursor-pointer opacity-0">
      {!options.some((o) => String(o.value) === String(value ?? '')) && <option value={value ?? ''}>{label}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

// Tokens choisis dans la pastille ; « Autre valeur… » demande le nombre.
const tokensFrom = (raw, current) => {
  if (raw !== 'autre') return Number(raw);
  const value = Number(window.prompt('Combien de tokens ?', String(current || '')));
  return value > 0 ? value : null;
};

const KickoffCard = ({
  objective, step, sprint, owner, sprintOptions, ownerOptions, tokenOptions, review, estimate, onPatch, onRemove,
  onReview, onAccept, onDismiss, onDragStart, onDragEnd,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(objective.name);
  const input = useRef(null);
  useEffect(() => { setDraft(objective.name); }, [objective.name]);
  useEffect(() => { if (editing) input.current?.select(); }, [editing]);

  const save = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== objective.name) onPatch({ name: next });
    else setDraft(objective.name);
  };

  const proposal = !objective.effort && (estimate && estimate !== 'pending' ? estimate.tokens : objective.predicted_tokens);
  const examples = estimate && estimate !== 'pending' ? estimate.examples || [] : [];
  const sprintChip = step !== 'sprints' && (
    <ChipSelect label={sprint ? sprint.short : 'sans sprint'} title="Sprint" value={objective.sprint_id || ''}
      options={[{ value: '', label: 'Sans sprint' }, ...sprintOptions]}
      onChange={(v) => onPatch({ sprint_id: v ? Number(v) : null })}
      tone={sprint ? `${sprint.color.soft} ${sprint.color.text}` : 'bg-slate-100 text-slate-400'} />
  );

  return (
    <article draggable={!editing} onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(objective.id)); onDragStart?.(objective); }}
      onDragEnd={onDragEnd}
      className="group relative cursor-grab rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:shadow active:cursor-grabbing">
      <div className="flex items-start gap-2">
        {STATUS_DOT[objective.status] && <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[objective.status]}`} title={objective.status === 'blocked' ? 'Bloqué' : 'En cours'} />}
        {editing ? (
          <textarea ref={input} value={draft} rows={2} onChange={(e) => setDraft(e.target.value)} onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
              if (e.key === 'Escape') { setDraft(objective.name); setEditing(false); }
            }}
            className="min-w-0 flex-1 resize-none rounded-md border border-indigo-200 px-1.5 py-1 text-[13px] leading-snug outline-none ring-2 ring-indigo-50" />
        ) : (
          <p lang="fr" onDoubleClick={() => setEditing(true)} className="min-w-0 flex-1 hyphens-auto break-words text-[13px] font-medium leading-snug text-slate-800">{objective.name}</p>
        )}
        <div className="absolute right-1 top-1 hidden items-center rounded-lg bg-white/95 shadow-sm ring-1 ring-slate-200 group-hover:flex">
          <button type="button" onClick={() => setEditing(true)} title="Renommer" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil size={12} /></button>
          <button type="button" onClick={onReview} title="Relire avec Nexus" className="rounded p-1 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"><Sparkles size={12} /></button>
          <button type="button" onClick={() => window.confirm(`Supprimer « ${objective.name} » ?`) && onRemove()} title="Supprimer"
            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={12} /></button>
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        {objective.effort > 0 ? (
          <ChipSelect label={points(objective.effort)} title="Tokens" value={objective.effort}
            options={[{ value: 0, label: 'À estimer' }, ...tokenOptions, { value: 'autre', label: 'Autre valeur…' }]}
            onChange={(v) => { const effort = tokensFrom(v, objective.effort); if (effort !== null) onPatch({ effort }); }}
            tone="bg-slate-900 text-white" />
        ) : estimate === 'pending' ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-px text-[11px] text-indigo-500">
            <Loader2 size={10} className="animate-spin" /> Nexus estime
          </span>
        ) : proposal ? (
          <button type="button" onClick={() => onPatch({ effort: proposal })}
            title={[
              "Proposition de Nexus : clic pour l'appliquer",
              estimate?.estimated_as && estimate.estimated_as !== objective.name ? `Estimé sur « ${estimate.estimated_as} »` : null,
              examples.length ? `Comme :\n${examples.map((e) => `• ${e.name} (${e.tokens})`).join('\n')}` : null,
            ].filter(Boolean).join('\n')}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-300 bg-indigo-50/60 px-1.5 py-px text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
            <Sparkles size={10} /> {points(proposal)} ?
          </button>
        ) : (
          <ChipSelect label="à estimer" title="Tokens" value={0}
            options={[{ value: 0, label: 'À estimer' }, ...tokenOptions, { value: 'autre', label: 'Autre valeur…' }]}
            onChange={(v) => { const effort = tokensFrom(v, 0); if (effort !== null) onPatch({ effort }); }} tone="bg-slate-100 text-slate-400" />
        )}
        {sprintChip}
        <span className="ml-auto">
          {owner ? (
            <label className="relative block cursor-pointer" title={`Responsable : ${owner.name}`}>
              <Avatar name={owner.name} picture={owner.picture} size="h-5 w-5" className="ring-1" />
              <select value={objective.owner_id || ''} onChange={(e) => onPatch({ owner_id: e.target.value ? Number(e.target.value) : null })}
                aria-label="Responsable" className="absolute inset-0 cursor-pointer opacity-0">
                <option value="">Sans responsable</option>
                {ownerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          ) : step !== 'owners' && (
            <ChipSelect label="qui ?" title="Responsable" value="" options={[{ value: '', label: 'Sans responsable' }, ...ownerOptions]}
              onChange={(v) => onPatch({ owner_id: v ? Number(v) : null })} tone="bg-slate-50 text-slate-400" />
          )}
        </span>
      </div>

      {review === 'pending' && (
        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-indigo-500"><Loader2 size={10} className="animate-spin" /> Nexus relit le titre…</p>
      )}
      {review && review !== 'pending' && review.suggestion && (
        <div className="mt-2 rounded-lg bg-indigo-50/70 px-2 py-1.5">
          <p className="flex gap-1 text-[12px] leading-snug text-indigo-900">
            <Sparkles size={11} className="mt-0.5 shrink-0 text-indigo-500" />
            <span>{review.suggestion}</span>
          </p>
          <div className="mt-1 flex justify-end gap-1">
            <button type="button" onClick={onDismiss} className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-white">
              <X size={11} /> Garder
            </button>
            <button type="button" onClick={onAccept} className="flex items-center gap-0.5 rounded bg-indigo-600 px-1.5 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-700">
              <Check size={11} /> Remplacer
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export default KickoffCard;
